import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Ticket as TicketIcon, Clock, User, MessageCircle, X, Send } from "lucide-react";

const PRIORITY_COLORS = {
  urgent: "bg-red-500/20 text-red-600 border-red-500/30",
  high: "bg-orange-500/20 text-orange-600 border-orange-500/30",
  medium: "bg-amber-500/20 text-amber-600 border-amber-500/30",
  low: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};
const STATUS_COLORS = {
  open: "bg-blue-500/20 text-blue-600",
  in_progress: "bg-purple-500/20 text-purple-600",
  waiting_customer: "bg-amber-500/20 text-amber-600",
  resolved: "bg-emerald-500/20 text-emerald-600",
  closed: "bg-slate-500/20 text-slate-300",
};

export default function AdminTickets() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [openTicket, setOpenTicket] = useState(null);
  const load = useCallback(async () => {
    const params = { ...(status ? { status } : {}), ...(priority ? { priority } : {}) };
    setRows((await api.get("/admin/tickets", { params })).data);
  }, [status, priority]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4 text-foreground" data-testid="admin-tickets">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// TICKETS</div>
          <h1 className="text-3xl font-bold mt-1">Support desk</h1>
          <div className="text-muted-foreground text-sm mt-1">SLA-tracked support tickets across every tenant.</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 rounded-sm bg-card text-foreground border-border" data-testid="ticket-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["open", "in_progress", "waiting_customer", "resolved", "closed"].map((s) => (
                <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 rounded-sm bg-card text-foreground border-border" data-testid="ticket-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {["urgent", "high", "medium", "low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-sm bg-card border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-card">
            <tr>
              <th className="text-left p-3">Ticket</th>
              <th className="text-left p-3">Tenant</th>
              <th className="text-left p-3">Priority</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">SLA</th>
              <th className="text-left p-3">Raised</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((t) => (
              <tr key={t.id} onClick={() => setOpenTicket(t)} className="cursor-pointer hover:bg-muted/60" data-testid={`ticket-row-${t.number}`}>
                <td className="p-3">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-[10px] font-mono text-muted-foreground/80">{t.number}</div>
                </td>
                <td className="p-3 text-foreground/80">{t.org_name}</td>
                <td className="p-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span></td>
                <td className="p-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[t.status]}`}>{t.status.replaceAll("_", " ")}</span></td>
                <td className="p-3 text-[11px] font-mono text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{t.sla_due_at?.slice(11, 16)} · {t.sla_due_at?.slice(0, 10)}</td>
                <td className="p-3 text-[11px] font-mono text-muted-foreground">{t.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground/80 italic">No tickets match</td></tr>}
          </tbody>
        </table>
      </Card>

      {openTicket && <TicketDrawer ticket={openTicket} onClose={() => setOpenTicket(null)} onChanged={load} />}
    </div>
  );
}

function TicketDrawer({ ticket, onClose, onChanged }) {
  const [t, setT] = useState(ticket);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = async () => setT((await api.get(`/tickets/${ticket.id}`)).data);
  const update = async (patch) => {
    setBusy(true);
    try { await api.patch(`/admin/tickets/${ticket.id}`, patch); await reload(); onChanged(); toast.success("Updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const post = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/comments`, { body: comment });
      setComment(""); await reload(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0A0A0F] border-l border-border h-full overflow-y-auto p-5 text-foreground" data-testid="ticket-drawer">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">{t.number}</div>
            <div className="text-xl font-bold">{t.title}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground hover:text-foreground" /></button>
        </div>
        <div className="text-[11px] font-mono text-muted-foreground mb-3">
          Raised by {t.raised_by?.email} · {t.created_at?.slice(0, 16).replace("T", " ")}
        </div>
        <div className="bg-card border border-border rounded-sm p-3 text-sm whitespace-pre-wrap mb-4">{t.body}</div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <Select value={t.status} onValueChange={(v) => update({ status: v })}>
            <SelectTrigger className="rounded-sm bg-card text-foreground border-border" data-testid="drawer-status"><SelectValue /></SelectTrigger>
            <SelectContent>{["open", "in_progress", "waiting_customer", "resolved", "closed"].map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={t.priority} onValueChange={(v) => update({ priority: v })}>
            <SelectTrigger className="rounded-sm bg-card text-foreground border-border" data-testid="drawer-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{["urgent", "high", "medium", "low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 mb-2">Conversation ({t.comments?.length || 0})</div>
        <div className="space-y-2 mb-4">
          {(t.comments || []).map((c) => (
            <div key={c.id} className={`rounded-sm p-3 border ${c.is_admin ? "bg-orange-500/10 border-orange-500/30" : "bg-card border-border"}`}>
              <div className="text-[10px] font-mono text-muted-foreground mb-1">
                {c.is_admin ? "🛡 " : ""}{c.author?.email} · {c.at?.slice(0, 16).replace("T", " ")}
              </div>
              <div className="text-sm whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t border-border pt-3">
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
            placeholder="Reply as super-admin…" className="flex-1 bg-background border border-border rounded-sm p-2 text-sm text-foreground" data-testid="drawer-reply" />
          <Button onClick={post} disabled={busy || !comment.trim()} className="rounded-sm bg-orange-500 hover:bg-orange-500/90" data-testid="drawer-reply-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
